import { useCallback, useMemo } from 'react';
import {
  Control,
  FieldError,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormElement } from '../FormElement';
import { InputText } from '../Inputs/InputText';
import { InputCheckBox } from '../Inputs/InputCheckBox';
import Button from '../core/Button';
import { IconCheck, IconCheckCircle } from '@/icons';
import { Spinner } from '../core/Spinner';
import { DeliveryOption } from './DeliveryOption';
import { isValidEmail } from '@/helpers/validation';
import type {
  CompleteOffering,
  DeliveryMethod,
  DeliveryMethodInfo,
} from '@/types/offering';
import type { OfferingFormValues } from './TicketOfferForm';
import Input from '../core/Input';

interface OfferingDeliverySectionProps {
  offering: CompleteOffering;
  deliveryMethod: DeliveryMethod;
  onChangeDeliveryMethod: (value: DeliveryMethod) => void;
  control: Control<OfferingFormValues>;
  watch: UseFormWatch<OfferingFormValues>;
  setValue: UseFormSetValue<OfferingFormValues>;
  getValues: UseFormGetValues<OfferingFormValues>;
  getFieldError: (
    fieldName: keyof OfferingFormValues,
  ) => FieldError | undefined;
  isLoadingTravellers: boolean;
  isLoadingBearers: boolean;
  isSubmitting: boolean;
}

export const OfferingDeliverySection = ({
  offering,
  deliveryMethod,
  onChangeDeliveryMethod,
  control,
  watch,
  setValue,
  getValues,
  getFieldError,
  isLoadingTravellers,
  isLoadingBearers,
  isSubmitting,
}: OfferingDeliverySectionProps) => {
  const { formatMessage } = useIntl();
  const watchTermsAccepted = watch('termsAccepted');
  const watchEmail = watch('email');
  const watchConfirmationEmail = watch('confirmationEmail');

  const invalidEmail =
    !!watchEmail &&
    isValidEmail(watchEmail) &&
    !isLoadingTravellers &&
    !watch('travellerId');

  const invalidCardNumber =
    !!watch('cardNumber') && !isLoadingBearers && !watch('cardBearerId');

  const confirmationEmailTrimmed = watchConfirmationEmail?.trim() ?? '';
  const confirmationEmailValid =
    !confirmationEmailTrimmed || isValidEmail(confirmationEmailTrimmed);
  const confirmationEmailRequired =
    deliveryMethod === 'other' || offering.transactions_tracked_externally;

  const submitDisabled =
    !watchTermsAccepted ||
    (deliveryMethod === 'app' && !watch('travellerId')) ||
    (deliveryMethod === 'card' && !watch('cardBearerId')) ||
    (confirmationEmailRequired &&
      (!confirmationEmailTrimmed || !isValidEmail(confirmationEmailTrimmed))) ||
    (!confirmationEmailRequired &&
      !!confirmationEmailTrimmed &&
      !confirmationEmailValid);

  const emailConfirmationLabel = useMemo(() => {
    if (offering.transactions_tracked_externally) {
      return formatMessage({
        id: 'ticketOffer.confirmationEmailAllTix',
        defaultMessage:
          'E-post för bekräftelse och självadministration av biljetten *',
      });
    }
    if (confirmationEmailRequired) {
      return formatMessage({
        id: 'ticketOffer.confirmationEmail',
        defaultMessage: 'E-post för bekräftelse',
      });
    }
    return formatMessage({
      id: 'ticketOffer.confirmationEmailOptional',
      defaultMessage: 'E-post för bekräftelse (valfritt)',
    });
  }, [
    confirmationEmailRequired,
    formatMessage,
    offering.transactions_tracked_externally,
  ]);

  const handleAppEmailBlur = useCallback(() => {
    const email = getValues('email').trim();
    if (!email) {
      return;
    }
    setValue('confirmationEmail', email, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [getValues, setValue]);

  const renderMethodContent = (method: DeliveryMethodInfo) => {
    switch (method.type) {
      case 'app':
        return (
          <>
            {method.description && <p>{method.description}</p>}
            <FormElement
              className="pt-5"
              required
              label={formatMessage({
                id: 'ticketOffer.emailAddress',
                defaultMessage: 'E-postadress',
              })}
              id="email"
              input={
                <div className="flex items-center gap-2">
                  <InputText
                    name="email"
                    control={control}
                    editMode={true}
                    error={getFieldError('email')}
                    onBlur={handleAppEmailBlur}
                    placeholder="exempel@email.se"
                  />
                  <div className="w-5">
                    {isLoadingTravellers && (
                      <Spinner className="size-5" aria-hidden="true" />
                    )}
                    {!isLoadingTravellers && watch('travellerId') && (
                      <IconCheckCircle className="size-5 text-green-500" />
                    )}
                  </div>
                </div>
              }
            />
            {invalidEmail && (
              <p className="text-red-500">
                <FormattedMessage
                  id="ticketOffer.accountNotFound"
                  defaultMessage="Kunde inte hitta ett konto. Kontrollera att du skrivit rätt email och att kontot är registrerat i appen."
                />
              </p>
            )}
          </>
        );
      case 'card':
        return (
          <>
            {method.description && <p>{method.description}</p>}
            <FormElement
              input={
                <div className="flex items-center gap-2 pt-5">
                  <InputText
                    name="cardNumber"
                    label={formatMessage({
                      id: 'ticketOffer.cardNumber',
                      defaultMessage: 'Kortnummer',
                    })}
                    placeholder="1234 5678 9012 3456"
                    control={control}
                    editMode={true}
                    error={getFieldError('cardNumber')}
                  />
                  <div className="w-5">
                    {isLoadingBearers && (
                      <Spinner className="size-5" aria-hidden="true" />
                    )}
                    {!isLoadingBearers && watch('cardBearerId') && (
                      <IconCheckCircle className="size-5 text-green-500" />
                    )}
                  </div>
                </div>
              }
            />
            {invalidCardNumber && (
              <p className="text-red-500">
                <FormattedMessage
                  id="ticketOffer.cardNotFound"
                  defaultMessage="Kunde inte hitta kortet"
                />
              </p>
            )}
          </>
        );
      case 'other':
        return method.description ? <p>{method.description}</p> : null;
      default:
        return null;
    }
  };

  return (
    <>
      <label className="mt-4 font-bold">
        <FormattedMessage
          id="ticketOffer.deliveryMethodQuestion"
          defaultMessage="Hur vill du ha biljetten?"
        />
      </label>

      {offering.deliveryMethods.map((method) => (
        <DeliveryOption
          key={method.type}
          wantedValue={method.type}
          value={deliveryMethod}
          onChange={onChangeDeliveryMethod}
          title={method.title}
        >
          {renderMethodContent(method)}
        </DeliveryOption>
      ))}

      <div>
        <Input
          id="confirmationEmail"
          type="email"
          className="mt-10"
          label={emailConfirmationLabel}
          value={watchConfirmationEmail}
          onChange={(e) =>
            setValue('confirmationEmail', e.target.value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
          placeholder="exempel@email.se"
          error={getFieldError('confirmationEmail')?.message}
          helperTextReserveSpace
        />
      </div>

      <FormElement
        className="mt-5 px-5"
        input={
          <InputCheckBox
            label={formatMessage({
              id: 'ticketOffer.termsDeclaration',
              defaultMessage:
                'Jag intygar att jag fyllt i korrekt information ovan samt förstår att erbjudandet enbart får lov att nyttjas av den valda mottagaren.',
            })}
            name="termsAccepted"
            control={control}
            editMode={true}
            error={getFieldError('termsAccepted')}
            onChange={() =>
              setValue('termsAccepted', !watchTermsAccepted, {
                shouldDirty: true,
              })
            }
          />
        }
      />

      <Button
        variant="filled"
        startIcon={<IconCheck />}
        type="submit"
        disabled={submitDisabled}
        className="mt-4 w-full py-3"
        loading={isSubmitting}
      >
        <FormattedMessage
          id="ticketOffer.placeOrder"
          defaultMessage="Beställ"
        />
      </Button>
    </>
  );
};
